/**
 * The one STATUS rule for a schedule row.
 *
 * Checked top to bottom, first match wins, against the row's own columns:
 * START, FINISH, BL START, BL FINISH, ACT START, ACT FINISH and PROG%.
 * WBS rows use the same ladder — their dates are already rolled up from their
 * children. Today is the data date; Tol is the slip tolerance in days.
 */

export const STATUS_TOLERANCE_DAYS = 5;

export const TASK_STATUSES = [
  'Completed – On Time',
  'Completed – Late',
  'On Track',
  'At Risk',
  'Delayed',
  'Overdue',
  'Late Start',
  'Forecast Delay',
  'Not Started – Overdue',
  'Not Started',
];

export const STATUS_STYLES = {
  'Completed – On Time': { short: 'On Time', text: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  'Completed – Late': { short: 'Late', text: 'text-amber-400', bg: 'bg-amber-500/20' },
  'On Track': { short: 'On Track', text: 'text-blue-400', bg: 'bg-blue-500/20' },
  'At Risk': { short: 'At Risk', text: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  'Delayed': { short: 'Delayed', text: 'text-orange-400', bg: 'bg-orange-500/20' },
  'Overdue': { short: 'Overdue', text: 'text-red-400', bg: 'bg-red-500/20' },
  'Late Start': { short: 'Late Start', text: 'text-orange-400', bg: 'bg-orange-500/20' },
  'Forecast Delay': { short: 'Fc Delay', text: 'text-amber-400', bg: 'bg-amber-500/20' },
  'Not Started – Overdue': { short: 'NS Overdue', text: 'text-red-400', bg: 'bg-red-500/20' },
  'Not Started': { short: 'Pending', text: 'text-slate-400', bg: 'bg-slate-500/20' },
};

const DAY_MS = 86400000;

/** Dates are compared by calendar day, read as written — the API sends UTC
 *  midnight, so local getters would shift the day west of Greenwich. */
const day = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** How far through its baseline the row should be by today, 0–1. */
export const plannedFraction = (blStart, blFinish, today) => {
  const bs = day(blStart);
  const bf = day(blFinish);
  if (bs === null || bf === null) return null;
  return Math.min(1, Math.max(0, (today - bs) / Math.max(DAY_MS, bf - bs)));
};

export function computeTaskStatus(data, options = {}) {
  if (!data) return 'Not Started';
  const tol = (options.tolDays ?? STATUS_TOLERANCE_DAYS) * DAY_MS;
  // Today is the data date when the schedule carries one, else the clock.
  const t = day(options.today) ?? day(new Date());

  const f = day(data.scheduled_finish);
  const bs = day(data.baseline_start);
  const bf = day(data.baseline_finish);
  const as = day(data.actual_start);
  const af = day(data.actual_finish);
  const p = (data.percent_complete || 0) / 100;

  if (af !== null) {
    // With no baseline there is nothing to be late against.
    return bf !== null && af > bf ? 'Completed – Late' : 'Completed – On Time';
  }

  if (as !== null) {
    if (f !== null && f < t) return 'Overdue';
    if (f !== null && bf !== null && f > bf + tol) return 'Delayed';
    const planned = plannedFraction(data.baseline_start, data.baseline_finish, t);
    if ((f !== null && bf !== null && f > bf)
        || (planned !== null && p < planned - 0.1)) return 'At Risk';
    return 'On Track';
  }

  if (bf !== null && t > bf) return 'Not Started – Overdue';
  if (bs !== null && t > bs) return 'Late Start';
  if (f !== null && bf !== null && f > bf) return 'Forecast Delay';
  return 'Not Started';
}
